import { DateTime } from "luxon";
import { IGanttChartSerieTemporal } from "../../stores/data/oceanWeatherIntelligences/oceanWeatherIntelligenceModel";

const HOUR = 3600 * 1000;
const DAY = 24 * HOUR;

/**
 * The Gantt's x-axis window.
 *
 * `start`/`end` are the response's evaluated range, and they are `0` when no row has
 * one -- every event bar-less (`calculating`, `unavailable`) or, on a past date, every
 * area `pastUnavailable` (aurora#1619).
 *
 * ⚠️ The inline version was `start - 6h || today`. With `start === 0` that is
 * `-21600000`, which is TRUTHY, so the fallback never ran and the axis opened on
 * 31/12/1969. 📏 Seen in the local smoke of #1619: "Belov Engenharia" retroacted to
 * 15/08/2026 drew a 1969-2020 axis.
 *
 * `anchor` is where an empty chart opens: the start of the retroacted day when one is
 * selected, today otherwise -- an empty past chart opening on today would sit next to
 * a date picker that says August.
 */
export const ganttXAxisRange = (
  start: number | undefined,
  end: number | undefined,
  anchor: number,
): { min: number; max: number } => ({
  min: start ? start - 6 * HOUR : anchor,
  max: end ? end : anchor + 5 * DAY,
});

/**
 * Where an empty Gantt opens: the start of the retroacted DAY, in the user's configured
 * zone -- or `fallback` (today) when no past date is selected.
 *
 * ⚠️ The zone is the point (Copilot review of #1325). The date picker stores the picked
 * wall time shifted by (configured zone - browser zone) -- `handleSelectForecastDate
 * Available` -- so the DateTime carries the right INSTANT in the BROWSER's zone.
 * `startOf("day")` there anchors to the browser's day: with UTC configured and a UTC-3
 * browser, a pick of 15/08 00:45 opened the axis on 14/08. The chart draws in the
 * configured zone (`time.timezoneOffset`), so the day is taken there.
 */
export const ganttAxisAnchor = (
  selected: DateTime | undefined,
  timezone: string | undefined,
  fallback: number,
): number => {
  if (!selected) return fallback;
  const inZone = timezone ? selected.setZone(timezone) : selected;
  // An unknown zone name yields an invalid DateTime; the browser's day beats NaN.
  const day = (inZone.isValid ? inZone : selected).startOf("day").toMillis();
  return Number.isNaN(day) ? fallback : day;
};

/**
 * Whether the Gantt should say "create your first event" (`almostThereCreateInsight
 * VisualizeChart`).
 *
 * It reads "no evaluated range and nothing processing" as "no events" -- which was the
 * only way to be bar-less before workspace#385. Rows carrying a `rowState` are events
 * too, and the state already says why they have no bar ("calculando", "sem cálculo
 * para esta data"). Without this exclusion an area-only dashboard retroacted to before
 * the engine served areas told the user to create events it already had.
 */
export const isGanttWithoutEvents = (
  data:
    | Pick<IGanttChartSerieTemporal, "isProcessing" | "rowState">[]
    | undefined,
  end: number | undefined,
  done: boolean,
): boolean =>
  !!data?.length &&
  end === 0 &&
  done &&
  !data.some((item) => item?.isProcessing) &&
  !data.some((item) => item?.rowState);
